/** @jsx jsx */
import { Hr, Spacer } from "@cd2/components/build/es6"
import { P } from "@cd2/text/build/es6"
import { jsx } from "@emotion/core"
import React from "react"
import { EventList } from "../Components/EventsList"
import { KeyValueList } from "../Components/KeyValueList"
import { TextWithNewLines } from "../Components/TextWithNewLines"
import { buildRoute, eRoutes } from "../routes"
import { IRecord } from "../types/schema"
import { HoverResourceLoader } from "./HoverResourceLoader"

type ISubCustomer = IRecord<"sub_customers", "name"> & {
  customer?: IRecord<"customers", "account_on_hold" | "name" | "status_reason" | "sales_rep">
  countContacts: number
  countQuotes: number
  countOrders: number
}

interface IProps {
  id?: string
  initialData?: Partial<ISubCustomer>
  renderInlineLink?: "block" | "inline"
  inline?: boolean
  includeEventList?: boolean
  startExpanded?: boolean
  noLink?: boolean
  stopPropagation?: boolean
}

export function HoverSubCustomerLoader({
  id,
  initialData,
  renderInlineLink,
  inline,
  includeEventList,
  startExpanded,
  noLink,
  stopPropagation,
}: IProps) {
  const skeem = window.skeem

  async function loadData() {
    const customer = await skeem.fetchOne<ISubCustomer>({
      sub_customers: {
        attributes: [
          "name",
          {
            name: "customer",
            attributes: ["name", "account_on_hold", "sales_rep", "status_reason"],
          },
          { as: "countContacts", value: { count: { attr: "contacts" } } },
          { as: "countQuotes", value: { count: { attr: "quotes" } } },
          { as: "countOrders", value: { count: { attr: "orders" } } },
        ],
        filter: { matches: { id } },
      },
    })
    return customer
  }

  if (!id) {
    return <span>-</span>
  }

  return (
    <React.Fragment>
      <HoverResourceLoader
        reloadKey={id}
        loadData={loadData}
        initialData={initialData}
        viewLink={
          noLink
            ? undefined
            : customer => buildRoute(eRoutes["/sub-customers/:id"], { id: customer.id })
        }
        resourceName="Customer"
        renderHoverContentTitle={record => (
          <React.Fragment>
            {record.name}
            {record.customer?.account_on_hold && <Spacer />}
            {record.customer?.account_on_hold && (
              <P weight={600}>
                This account is on hold:
                {record.customer?.status_reason && (
                  <React.Fragment>
                    <br />
                    <b>
                      <TextWithNewLines text={record.customer?.status_reason} />
                    </b>
                  </React.Fragment>
                )}
              </P>
            )}
          </React.Fragment>
        )}
        renderInlineLink={renderInlineLink}
        inline={inline}
        color={data => (data?.customer?.account_on_hold ? "#fffbd2" : undefined)}
        renderHoverContent={record => (
          <div>
            <KeyValueList
              labelWidth={100}
              data={[
                {
                  label: "Customer",
                  value: record.customer
                    ? { hover: { model: "customer", id: record.customer?.id } }
                    : null,
                },
                {
                  label: "# Contacts",
                  value: record.countContacts,
                },
                {
                  label: "# Quotes",
                  value: record.countQuotes,
                },
                {
                  label: "# Orders",
                  value: record.countOrders,
                },
              ]}
              startExpanded={startExpanded}
              inline
            />
            {includeEventList && (
              <React.Fragment>
                <Spacer size={10} />
                <Hr />
                <Spacer size={10} />
                <EventList
                  id={record.id}
                  model="sub_customers"
                  limit={3}
                  viewAllLink={buildRoute(eRoutes["/sub-customers/:id/history"], { id })}
                />
              </React.Fragment>
            )}
          </div>
        )}
        renderTarget={record => (
          <span onClick={stopPropagation ? e => e.stopPropagation() : undefined}>
            {record.name}
          </span>
        )}
      />
    </React.Fragment>
  )
}
